import { LoaderFunction, ShouldRevalidateFunction } from 'react-router-dom';
import { client } from '~/api/client';
import { queryData } from '~/api/data/request';
import { loadQuery } from '~/api/data/responses';
import {
  GetSmartFillFormBucketQuestionsDocument,
  GetSmartFillFormBucketQuestionsQuery,
  GetSmartFillFormBucketQuestionsQueryVariables,
  GetSmartFillFormBucketsDocument,
  GetSmartFillFormBucketsQuery,
  GetSmartFillFormBucketsQueryVariables,
  GetSmartFillCarrierSitesDocument,
  GetSmartFillCarrierSitesQuery,
  GetSmartFillCarrierSitesQueryVariables,
  SmartFillFormBucketFieldsFragment,
  SmartFillFormBucketQuestionsFieldsFragment,
  SmartFillCarrierSiteFieldsFragment
} from '~/api/graphql';

export const getSmartFillFormBuckets = async () =>
  queryData<
    GetSmartFillFormBucketsQuery,
    GetSmartFillFormBucketsQueryVariables,
    SmartFillFormBucketFieldsFragment[]
  >(client, { query: GetSmartFillFormBucketsDocument }, (data) => data?.smartFillFormBuckets ?? []);

export const getSmartFillCarrierSites = async () =>
  queryData<
    GetSmartFillCarrierSitesQuery,
    GetSmartFillCarrierSitesQueryVariables,
    SmartFillCarrierSiteFieldsFragment[]
  >(
    client,
    { query: GetSmartFillCarrierSitesDocument },
    (data) => data?.smartFillCarrierSites ?? []
  );

export const getSmartFillFormBucketQuestions = async (
  bucketId: string,
  carrierSiteId: string | null
) =>
  queryData<
    GetSmartFillFormBucketQuestionsQuery,
    GetSmartFillFormBucketQuestionsQueryVariables,
    SmartFillFormBucketQuestionsFieldsFragment
  >(
    client,
    {
      query: GetSmartFillFormBucketQuestionsDocument,
      variables: { bucketId, carrierSiteId }
    },
    (data) => data?.smartFillFormBucketQuestions
  );

export const loadSmartFillFormBuckets: LoaderFunction = () => loadQuery(getSmartFillFormBuckets());

export const loadSmartFillCarrierSites: LoaderFunction = () =>
  loadQuery(getSmartFillCarrierSites());

/**
 * The bucket's questions for its line of business, or for the carrier site the URL
 * names when the agent configures it for one. A site the server does not know reads
 * as the line again.
 */
export const loadSmartFillFormBucketQuestions: LoaderFunction = async ({ params, request }) => {
  const carrierSites = await loadQuery(getSmartFillCarrierSites());
  if (carrierSites instanceof Response) {
    return carrierSites;
  }
  const requested = new URL(request.url).searchParams.get('site');
  const carrierSiteId = carrierSites?.find((site) => site.id === requested)?.id ?? null;
  return loadQuery(getSmartFillFormBucketQuestions(params.bucketId ?? '', carrierSiteId));
};

// A save returns the refreshed questions, which the page renders from, so there is
// nothing for the loader to read again.
export const shouldRevalidateSmartFillFormBucket: ShouldRevalidateFunction = ({
  formMethod,
  defaultShouldRevalidate
}) => (formMethod?.toUpperCase() === 'POST' ? false : defaultShouldRevalidate);
